import { Group, Object3D } from "three";

import { Leg, Square } from "src/api";
import { TypedEventEmitter, createTypedEventEmitter } from "src/shared/util";

import {
  DEFAULT_OUTLINE_COLOR,
  DEFAULT_OUTLINE_EDGE_STRENGTH
} from "../renderConstants";
import type { OutlineSettings } from "./OutlineSettings";

export type WorldObjectSessionDetails = {
  hovered?: boolean;
  selected?: boolean;
  targetedEnemy?: boolean;
  targetedAlly?: boolean;
};

export type WorldObjectAttackDetails = {
  fromSquare?: Square;
  toSquare?: Square;
};

export type WorldObjectMoveDetails = {
  fromSquare?: Square;
  toSquare?: Square;
};

export type WorldObjectDefaultEvents = {
  attacked: WorldObjectAttackDetails;
  attacking: WorldObjectAttackDetails;
  moved: WorldObjectMoveDetails;
  died: void;
};

export type WorldObjectAPI = {
  readonly root: Object3D;
  readonly events: TypedEventEmitter<WorldObjectDefaultEvents>;

  step?(deltaSeconds: number): void;
  dispose(): void;

  // This API is not ideal, as world objects should be in control of their own outline.
  getOutline?(): OutlineSettings | null;
  setOutline?(
    enabled: boolean,
    outlineSettings: Partial<OutlineSettings> | null
  ): void;

  // This API lets us push details from the current session onto objects.
  getSessionDetails?(): WorldObjectSessionDetails;
  setSessionDetails?(details: WorldObjectSessionDetails): void;

  // This API is used for piece-to-piece communication. Communication via slap.
  notifyAttacked?(leg: Leg): void;
  // This API is used to tell a piece it will die.
  // The return flag indicates whether it will signal with "died" when its animation completes.
  notifyDead?(): boolean;
};

export abstract class WorldObject implements WorldObjectAPI {
  readonly root = new Group();
  readonly events = createTypedEventEmitter<WorldObjectDefaultEvents>();

  private outline: OutlineSettings | null = null;

  // NOTE: called internally for the most part.
  setOutline(enabled: boolean, settings: Partial<OutlineSettings> | null = {}) {
    this.outline = enabled
      ? {
          color: DEFAULT_OUTLINE_COLOR,
          edgeStrength: DEFAULT_OUTLINE_EDGE_STRENGTH,
          ...settings
        }
      : null;
  }

  // NOTE: read by the renderer every frame to draw an outline.
  getOutline(): OutlineSettings | null {
    return this.outline;
  }

  step(_deltaSeconds: number) {}

  abstract dispose(): void;
}
